// Import media
import postLoopBannerImg from './img/post-loop-banner.svg';

// Import global utils
import { versionCompare } from '@automator/shared/utils/version-compare';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import recipe builder services
import { createLoop } from '@automator/builder/services/item/loop';

// Import dependencies
import { html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-loop-add-post-loop' )
export class AutomatorBuilderLoopAddPostLoop extends LitElement {
	@property( { type: String } ) accessor size;

	@property( { type: String } ) accessor color = 'secondary';

	// eslint-disable-next-line require-jsdoc
	render() {
		// If the button shouldn't render, return nothing
		if ( ! this.shouldRender() ) {
			return;
		}

		return html`
			<uo-button
				size=${ this.size }
				color=${ this.color }
				@click=${ this.handleClick }
			>
				${ this._templateLabel }
			</uo-button>
		`;
	}

	/**
	 * Returns the template for the label
	 *
	 * @return {TemplateResult} The template for the label
	 */
	get _templateLabel() {
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return html`${ escapeHTML( __( 'Post loop', 'uncanny-automator' ) ) }`;
		}

		return html`
			${ escapeHTML( __( 'Post loop', 'uncanny-automator' ) ) } <uo-pro-tag size=${ ifDefined( this.size ) }></uo-pro-tag>
		`;
	}

	/**
	 * Handles the click event on the button.
	 *
	 * This method is responsible for determining what to do when the user clicks the button.
	 * — If the user is not a Pro user, it will show the upsell dialog.
	 * — If the user is a Pro user, it will add a post loop to the recipe.
	 *
	 * @return {undefined}
	 */
	handleClick() {
		// If the user doesn't have Pro, show the upsell dialog
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			this.handleCreateUpsellDialog();
			return;
		}

		// Otherwise, add the user loop
		this.handleAddPostLoop();
	}

	/**
	 * Handles adding a loop too the recipe
	 */
	handleAddPostLoop() {
		// Add a user loop
		createLoop( {
			type: 'posts'
		} );
	}

	/**
	 * Creates the dialog that explains to non-Pro users what this feature does and how they can get it.
	 */
	handleCreateUpsellDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					heading=${ escapeHTML( __( 'Post loop', 'uncanny-automator' ) ) }
					featured-image=${ postLoopBannerImg }
					featured-image-position="center bottom"
					size="large"
					open
				>
					<p>
						${ escapeHTML( __( 'Post loops let you perform bulk actions on posts that meet specific criteria.', 'uncanny-automator' ) ) }
					</p>

					<p>
						${ escapeHTML( __( 'Here are a few examples of what you can do with post loops:', 'uncanny-automator' ) ) }
					</p>

					<ul>
						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Export **all blog posts, products or courses in a category** to a Google Sheet, AirTable or Notion.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Change **all posts of written by one author** to another author.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Update meta values on all **posts that match a specific meta value**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( "Automatically generate SEO descriptions with OpenAI for **all blog posts that don't have a meta description**.", 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>
					</ul>
					
					<uo-button
						slot="primary-action"

						href=${ this.upsaleURL }
						target="_blank"

						dialog-action="close"
					>
						${ escapeHTML( __( 'Get Post loops', 'uncanny-automator' ) ) }
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * The URL to the upsell page. This URL will include UTM parameters to track the user's journey.
	 *
	 * @return {String} The URL to the upsell page.
	 */
	get upsaleURL() {
		return addParametersToURL(
			// Add UTM parameters
			addParametersUTM( 'https://automatorplugin.com/pricing/', {
				medium: 'recipe_creator',
				content: 'add_post_loop_button',
			} ),
			// Add context to show banner in the pricing page
			{
				uap_bpro_context: 'post_loop'
			}
		)
	}

	/**
	 * Determines whether or not the button should render
	 *
	 * @return {Boolean} Whether or not the button should render
	 */
	shouldRender() {
		// If the user doesn't have Pro, render it because we're going to upsell the feature
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return true;
		}

		// If the user has Pro, only render it if they have version 5.3 or higher
		return versionCompare(
			UncannyAutomator._site.automator.version_pro,
			'5.3',
			'>='
		);
	}
}
